'use client';

import React, { useEffect, useId, useRef, useState } from 'react';
import { classnames } from '@/utils/classnames/classnames';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useTranslations } from 'use-intl';
import { MAX_SEARCH_QUERY_LENGTH as MAX_QUERY_LENGTH } from '@/lib/algolia/query-limits';
import { SearchPageHeaderProps } from './types';

const SearchPageHeader = ({ query, onQuerySubmit, autoFocus = false }: SearchPageHeaderProps) => {
  const translate = useTranslations();
  const inputId = useId();
  const errorId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [value, setValue] = useState(query);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Sync the local input value when the parent's query changes (e.g. browser
  // back/forward updates `?query=` without re-mounting the molecule).
  // EWM-760 — adjusted during render instead of a cascading `useEffect` —
  // same trigger (`query` identity change), one render sooner.
  const [prevQuery, setPrevQuery] = useState(query);
  if (prevQuery !== query) {
    setPrevQuery(query);
    setValue(query);
    setValidationError(null);
  }

  // EWM-242: focus the input on mount when the caller asks (empty-/search).
  // Only fires once — useEffect's dependency on `autoFocus` (boolean, stable
  // for an empty-state render) avoids re-stealing focus on rerenders.
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const trimmed = value.trim();
    if (trimmed.length > MAX_QUERY_LENGTH) {
      // Block submit + surface the AC error. The trimmed length is what
      // we'd send to the BFF, so use it (not the raw value).
      setValidationError(translate('search.query-too-long', { max: MAX_QUERY_LENGTH }));
      inputRef.current?.focus();
      return;
    }
    setValidationError(null);
    if (trimmed === query) return;
    onQuerySubmit(trimmed);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
    // Clear the inline error once the user is back within budget.
    if (validationError && e.target.value.trim().length <= MAX_QUERY_LENGTH) {
      setValidationError(null);
    }
  };

  return (
    <div className="mb-6">
      <h1 className="text-primary text-32 mb-4 font-bold">{translate('search.title')}</h1>

      <form role="search" onSubmit={handleSubmit} className="relative" noValidate>
        <MagnifyingGlassIcon
          aria-hidden="true"
          className="pointer-events-none absolute top-1/2 left-5 h-6 w-6 -translate-y-1/2 text-gray-400"
        />
        <label htmlFor={inputId} className="sr-only">
          {translate('search.input-aria-label')}
        </label>
        <input
          id={inputId}
          ref={inputRef}
          type="search"
          value={value}
          onChange={handleChange}
          placeholder={translate('search.input-placeholder')}
          aria-label={translate('search.input-aria-label')}
          aria-invalid={validationError ? 'true' : undefined}
          aria-describedby={validationError ? errorId : undefined}
          autoComplete="off"
          className={classnames(
            'text-18 text-primary placeholder:text-14 focus:ring-primary/20 w-full rounded-lg border bg-white py-4 pr-4 pl-14 font-semibold placeholder:font-normal placeholder:text-gray-400 focus:ring-2 focus:outline-none',
            validationError ? 'border-red-500 focus:border-red-500' : 'focus:border-primary border-gray-300',
          )}
        />
      </form>

      {validationError && (
        <p id={errorId} className="text-13 mt-2 text-red-600" role="alert">
          {validationError}
        </p>
      )}
    </div>
  );
};

export default SearchPageHeader;
